import { Link as RouterLink } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  Collapse,
  Tag,
  Tooltip,
  useTailwindBreakpoint,
} from '@eye-able/ui';
import { useMemo, useState } from 'react';
import {
  useHomeApplications,
  type Application,
} from '../hooks/useHomeApplications';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

function HomeApplicationsBanner() {
  const { t } = useInlineTranslation();

  const applications = useHomeApplications();

  const isMobile = !useTailwindBreakpoint('md');
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  const visibleApplications = useMemo(() => {
    if (!isMobile) {
      return applications;
    }

    return applications.slice(0, 6);
  }, [applications, isMobile]);

  const collapsedApplications = useMemo(() => {
    if (!isMobile) {
      return [];
    }

    return applications.slice(6);
  }, [applications, isMobile]);

  const hasCollapsedApps = collapsedApplications.length > 0;

  const renderApplication = ({
    id,
    icon: Icon,
    label,
    description,
    link,
    isNew,
    hasLicense,
  }: Application) => {
    return (
      <li key={id}>
        <Tooltip content={t(description)}>
          <RouterLink
            to={link}
            className='eye:flex eye:flex-col eye:items-center eye:gap-3xs eye:no-underline eye:group'
          >
            <Badge
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'top',
              }}
              badgeClassName='eye:bg-blue-100 eye:text-white eye:size-24'
              badgeContent={
                hasLicense ? (
                  <Check aria-hidden />
                ) : isNew ? (
                  <Tag className='eye:ring-1'>{t('new')}</Tag>
                ) : null
              }
              offsetX={-20}
              offsetY={7}
            >
              <Avatar className='eye:size-72 eye:bg-black/5 eye:group-hover:bg-black eye:transition-colors'>
                <Icon
                  aria-hidden
                  className='eye:stroke-[1.5] eye:size-32 eye:group-hover:text-white'
                />
              </Avatar>
            </Badge>

            <span className='eye:text-center eye:body-m-highlight eye:wrap-anywhere'>
              {t(label)}
            </span>
          </RouterLink>
        </Tooltip>
      </li>
    );
  };

  return (
    <Card variant='highlight'>
      <CardHeader>
        <CardDescription>
          <ul className='eye:grid eye:grid-cols-3 eye:md:grid-cols-4 eye:lg:grid-cols-6 eye:gap-m'>
            {visibleApplications.map((app) => renderApplication(app))}
          </ul>

          {hasCollapsedApps ? (
            <Collapse
              timeout={300}
              open={isMobileExpanded}
              onOpenChange={setIsMobileExpanded}
            >
              <ul className='eye:grid eye:grid-cols-3 eye:md:grid-cols-4 eye:lg:grid-cols-6 eye:gap-m eye:pt-m'>
                {collapsedApplications.map((app) => renderApplication(app))}
              </ul>
            </Collapse>
          ) : null}

          {hasCollapsedApps ? (
            <div className='eye:mt-s eye:flex eye:justify-center'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setIsMobileExpanded((prev) => !prev)}
                aria-expanded={isMobileExpanded}
              >
                {isMobileExpanded ? t('showLess') : t('showMore')}
              </Button>
            </div>
          ) : null}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

export default HomeApplicationsBanner;
